import React, {ReactElement} from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CopyLinkButton
} from '@customink/pigment-react';
import {Box, styled, SvgIcon} from '@mui/material';
import EditIcon from '../../assets/icons/edit.svg?com';
import LinkIcon from '../../assets/icons/link.svg?com';
import services from '../../api/remoteServices';

type FirstOpenDialogProps = {
  storeSlug: string;
  open: boolean;
  onClose: (value: string) => void;
};

const DialogContentItem = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-start',
  gap: '0.75rem',
  margin: '1rem 0'
}));

const FirstOpenDialog: React.FC<FirstOpenDialogProps> = ({
  storeSlug,
  open,
  onClose
}: FirstOpenDialogProps): ReactElement => {
  const handleClose = () => {
    onClose(storeSlug);
  };

  const storeUrl = `${services.stores}/${storeSlug}`;

  return (
    <Dialog
      title="Your store is now open for business!"
      width="lg"
      onClose={handleClose}
      titleSx={{
        fontSize: '3rem',
        lineHeight: '3.5rem',
        fontWeight: '700',
        paddingBottom: '0.5rem'
      }}
      padding={{
        paddingTop: '2.5rem',
        paddingRight: '5rem',
        paddingBottom: '2.5rem',
        paddingLeft: '5rem'
      }}
      open={open}>
      <DialogContent>
        <Box sx={{margin: '1.5rem 0'}}>
          <Typography variant="bodyLong2">
            Nice job! Now that your new store is open, what&apos;s next?
          </Typography>
        </Box>
        <DialogContentItem>
          <SvgIcon color="secondary" alt="Edit" component={EditIcon} />
          <Typography variant="bodyLong2">
            You can continue to customize your store and add products
          </Typography>
        </DialogContentItem>
        <DialogContentItem>
          <SvgIcon color="secondary" alt="Link" component={LinkIcon} />
          <Typography variant="bodyLong2">
            Check out your{' '}
            <a
              href={storeUrl}
              target="_blank"
              rel="noreferrer"
              style={{fontWeight: 700}}>
              live store
            </a>{' '}
            and share your link:
          </Typography>
        </DialogContentItem>
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            paddingTop: '0.5rem',
            fontWeight: '700'
          }}>
          <Typography
            variant="h6"
            fontWeight="bolder"
            color="var(--color-slate)">
            {storeUrl}
          </Typography>
          <CopyLinkButton
            linkText={storeUrl}
            tooltipPlacement="top"
            color="secondary"
          />
        </Box>
      </DialogContent>
      <DialogActions justifyContent="flex-start" paddingTop="2.5rem">
        <Button
          size="large"
          variant="primary"
          sx={{padding: '0 4rem'}}
          href={storeUrl}
          target="_blank"
          rel="noreferrer"
          role="link">
          View Store
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FirstOpenDialog;
